import styled, { css } from 'styled-components'

import { EmailIcon } from '@nx/fire/assets'
import { FireImage } from '@nx/fire/image'
import { FireLink, FireLinkProps } from '@nx/fire/link'
import { Body, Heading, Label } from '@nx/fire/typography'

import { FirePersonCardProps } from './FirePersonCard.types'

interface Compact {
  $isAlwaysCompact: FirePersonCardProps['isAlwaysCompact']
}

interface ContactOnly {
  $isContactOnlyCard: boolean
}

type StyledTopWrapperProps = Compact & ContactOnly

export const StyledCard = styled.article<Compact>`
  box-shadow: ${({ theme }) => theme.shadows.small};
  background: ${({ theme }) => theme.colours.primary.white};
  text-align: left;
  min-width: 270px;
  width: 100%;
  padding: 20px 15px;
  border-radius: 3px;
  display: grid;
  grid-template-rows: 1fr min-content;
  height: 100%;

  ${({ $isAlwaysCompact, theme }) =>
    !$isAlwaysCompact &&
    css`
      ${theme.media.up.md} {
        text-align: center;
        min-width: 250px;
        width: 250px;
      }
    `}
`

export const StyledName = styled(Heading)<Compact>`
  color: ${({ theme }) => theme.colours.primary.black};
  margin-top: 0;

  ${({ $isAlwaysCompact, theme }) =>
    !$isAlwaysCompact &&
    css`
      ${theme.media.up.md} {
        margin-top: 5px;
      }
    `}
`

export const StyledJobRole = styled(Body)`
  color: ${({ theme }) => theme.colours.primary.black};
`

export const StyledContact = styled(Body)`
  color: ${({ theme }) => theme.colours.primary.black};
  margin-bottom: 0;
  text-overflow: ellipsis;
  overflow: hidden;
`

export const StyledEmailIcon = styled(EmailIcon)`
  color: ${({ theme }) => theme.colours.primary.primary};
  margin-top: 3px;
`

export const StyledPhoneLink = styled(FireLink)<FireLinkProps>`
  color: ${({ theme }) => theme.colours.primary.black};
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`

export const StyledEmailLink = styled(FireLink)<FireLinkProps>`
  text-decoration: underline;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colours.secondary.primary};
  }
`

export const StyledContactGrid = styled.div<Compact>`
  display: grid;
  align-items: center;
  grid-template-columns: min-content 1fr;
  gap: 3px;

  ${({ theme, $isAlwaysCompact }) =>
    !$isAlwaysCompact &&
    css`
      ${theme.media.up.md} {
        justify-content: center;
        grid-auto-flow: column;
        grid-template-columns: auto;
      }
    `}
`

export const StyledImageWrapper = styled.div<Compact>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  grid-row: 1/-1;
  background: ${({ theme }) => theme.colours.grey['45']};
  color: ${({ theme }) => theme.colours.primary.white};
  margin: 0 auto;
  overflow: hidden;
  position: relative;

  ${({ $isAlwaysCompact, theme }) =>
    !$isAlwaysCompact &&
    css`
      ${theme.media.up.md} {
        width: 115px;
        height: 115px;
      }
    `}
  > * {
    position: absolute;
    inset: 0;
  }
`

export const StyledImage = styled(FireImage)`
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
`

export const StyledDescription = styled(Body)`
  ${({ theme }) => theme.fontStyles.sourceSans.light}

  color: ${({ theme }) => theme.colours.primary.black};
  font-style: italic;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 66px;
`

export const StyledViewProfileLink = styled(Label)<Compact>`
  color: ${({ theme }) => theme.colours.primary.primary};
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  width: min-content;
  white-space: nowrap;

  svg {
    margin-left: 2px;
  }

  ${({ $isAlwaysCompact, theme }) =>
    !$isAlwaysCompact &&
    css`
      ${theme.media.up.md} {
        width: auto;
        justify-content: center;
      }
    `}
`

export const StyledTopWrapper = styled.div<StyledTopWrapperProps>`
  display: grid;
  grid-template-columns: 80px 1fr;
  grid-template-rows: auto 1fr;
  grid-column-gap: 15px;
  margin-bottom: 15px;

  ${({ $isAlwaysCompact, $isContactOnlyCard, theme }) =>
    !$isAlwaysCompact &&
    css`
      ${theme.media.up.md} {
        display: block;
        margin-bottom: ${!$isContactOnlyCard && 0};
      }
    `}
`
